import * as React from "react";

import { Stack } from "@mui/material";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";

import { IconElement } from "@/icons/types";

export interface TabOption<T> {
  value: T;
  label: string;
  icon?: IconElement;
  notification?: number;
  tooltip?: string;
  onChange?: () => void;
}

export interface LiveEditorTabProps<T, TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  tabs: TabOption<T>[];
}

export function LiveEditorTab<T, TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  tabs,
}: LiveEditorTabProps<T, TFieldValues>): React.ReactElement {
  const {
    field: { value, onChange, ...rest },
  } = useController({ name, control });

  const handleChange = (tab: TabOption<T>) => {
    onChange(tab.value);
    tab.onChange?.();
  };

  return (
    <Stack
      {...rest}
      direction="row"
      gap={0.5}
      sx={(theme) => ({
        width: "100%",
        backgroundColor: theme.palette.surface.neutral.quiet,
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0.5),
        borderRadius: theme.radius(1.5),
      })}
    >
      {tabs.map((tab) => {
        const isSelected = tab.value === value;

        return (
          <Stack
            key={String(tab.value)}
            onClick={() => handleChange(tab)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleChange(tab);
              }
            }}
            role="tab"
            tabIndex={0}
            aria-selected={isSelected}
            sx={(theme) => ({
              flex: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: theme.spacing(1, 2),
              borderRadius: theme.radius(1),
              ...theme.typography.subtitle1,
              backgroundColor: isSelected ? theme.palette.surface.neutral.white : "transparent",
              color: isSelected ? theme.palette.text.brand.intense : theme.palette.text.neutral.quiet,
              transition: "background-color 0.2s ease-in, color 0.2s ease-in",
              "&:hover": {
                backgroundColor: isSelected
                  ? theme.palette.surface.neutral.white
                  : theme.palette.surface.neutral.moderate,
              },
            })}
          >
            {tab.label}
          </Stack>
        );
      })}
    </Stack>
  );
}
